import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { Row, Col, Card, CardBody, CardHeader, Button } from 'reactstrap';
import { buildCabinetReplaceViewQuery } from '../../utils/cabinetDeepLinkFilters';
import CabinetFilter from './cabinetFilter';
import ControlFilterSwitch from '../common/controls/controlFilterSwitch';
import entityComposition from '../common/classes/entityComposition';
import Icon from '../common/containers/icon';
import FullTextInstructions from '../common/search/fullTextInstructions';
import SearchInput from '../common/search/searchInput';
import ShowDeletedFilter from '../common/filters/showDeletedFilter';
import AiChatModal from './aiChatModal';
import SavedFormFilters from './savedFormFilters';

// must be declared here so the ref callback can refer to it
let controlFilterSwitch = null;
const CabinetFilterPanel = _ref => {
  var _aiSearchConfig$promp, _aiSearchConfig$waiti, _aiSearchConfig$title, _aiSearchConfig$butto, _aiSearchConfig$empty, _indexQuery$filters$m, _indexQuery$filters, _indexQuery$filters2, _indexQuery$filters3, _indexQuery$filters4;
  let cabinetIdentifier = _ref.cabinetIdentifier,
    _ref$cabinetActions = _ref.cabinetActions,
    serverFilter = _ref$cabinetActions.serverFilter,
    serverReplaceView = _ref$cabinetActions.serverReplaceView,
    _ref$currentCabinet = _ref.currentCabinet,
    indexQuery = _ref$currentCabinet.indexQuery,
    aiError = _ref$currentCabinet.aiError,
    aiRequest = _ref$currentCabinet.aiRequest,
    aiSuccess = _ref$currentCabinet.aiSuccess,
    currentCabinet = _ref.currentCabinet,
    filterDefinitions = _ref.filterDefinitions,
    _ref$formTypeIdentifi = _ref.formTypeIdentifiers,
    formTypeIdentifiers = _ref$formTypeIdentifi === void 0 ? [] : _ref$formTypeIdentifi,
    cabinetFilterOptions = _ref.cabinetFilterOptions,
    cabinetFilterOptionsActions = _ref.cabinetFilterOptionsActions,
    _ref$searchPlaceholde = _ref.searchPlaceholder,
    searchPlaceholder = _ref$searchPlaceholde === void 0 ? 'Search' : _ref$searchPlaceholde,
    _ref$aiSearchConfig = _ref.aiSearchConfig,
    aiSearchConfig = _ref$aiSearchConfig === void 0 ? undefined : _ref$aiSearchConfig,
    _ref$username = _ref.username,
    username = _ref$username === void 0 ? '' : _ref$username;
  // Admin user check - easy to modify later to enable for all users
  // To enable for all users, simply change this to: const isAdminUser = true;
  const isAdminUser = username === 'truqc';
  // Merge aiSearchConfig with defaults to handle partial configurations
  const mergedAiSearchConfig = aiSearchConfig ? {
    promptPlaceholder: (_aiSearchConfig$promp = aiSearchConfig.promptPlaceholder) !== null && _aiSearchConfig$promp !== void 0 ? _aiSearchConfig$promp : 'Ask AI anything...',
    waitingMessage: (_aiSearchConfig$waiti = aiSearchConfig.waitingMessage) !== null && _aiSearchConfig$waiti !== void 0 ? _aiSearchConfig$waiti : 'Thinking...',
    title: (_aiSearchConfig$title = aiSearchConfig.title) !== null && _aiSearchConfig$title !== void 0 ? _aiSearchConfig$title : 'AI Search',
    buttonTitle: (_aiSearchConfig$butto = aiSearchConfig.buttonTitle) !== null && _aiSearchConfig$butto !== void 0 ? _aiSearchConfig$butto : 'AI Search',
    emptyStateMessage: (_aiSearchConfig$empty = aiSearchConfig.emptyStateMessage) !== null && _aiSearchConfig$empty !== void 0 ? _aiSearchConfig$empty : 'Start a conversation with AI'
  } : null;

  // Modal state
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isAiChatModalOpen = _useState2[0],
    setIsAiChatModalOpen = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    hoverTimer = _useState4[0],
    setHoverTimer = _useState4[1];
  const aiSearchButtonRef = useRef(null);
  const prevAiSuccessRef = useRef(false);
  /** Serialize filter-option prefetches across effect re-runs (stale `filters` deps). */
  const filterPrefetchRunIdRef = useRef(0);
  const filterPrefetchTailRef = useRef(Promise.resolve());

  // Convert AI error to chat message format
  const errorMessages = aiError ? [{
    id: "error-".concat(Date.now()),
    content: aiError,
    type: 'system',
    timestamp: new Date().toISOString()
  }] : [];
  const filterText = textValue => {
    const newFilters = _objectSpread(_objectSpread({}, (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}), {}, {
      text: textValue
    });
    // don't try to filter if indexQuery isn't initialized
    if (indexQuery) serverFilter(newFilters, indexQuery);
  };

  // Modal handlers
  const openAiChatModal = () => {
    setIsAiChatModalOpen(true);
  };
  const closeAiChatModal = () => {
    setIsAiChatModalOpen(false);
  };
  const filterAISearch = async aiSearchValue => {
    const newFilters = _objectSpread(_objectSpread({}, (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}), {}, {
      aiSearch: aiSearchValue
    });
    // don't try to filter if indexQuery isn't initialized
    if (indexQuery) {
      await serverFilter(newFilters, indexQuery);
    }
  };
  const handleAiSearchButtonClick = () => {
    openAiChatModal();
  };
  const handleAiSearchButtonMouseEnter = () => {
    const timer = setTimeout(() => {
      openAiChatModal();
    }, 800); // 0.8 seconds
    setHoverTimer(timer);
  };
  const handleAiSearchButtonMouseLeave = () => {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      setHoverTimer(null);
    }
  };

  // Clean up timer on unmount
  useEffect(() => () => {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
    }
  }, [hoverTimer]);

  // Close modal only when AI success transitions from false -> true
  useEffect(() => {
    if (aiSuccess && !prevAiSuccessRef.current) {
      closeAiChatModal();
    }
    prevAiSuccessRef.current = aiSuccess;
  }, [aiSuccess]);

  /**
   *  Passed to CabinetFilter and on to BreadcrumbsBack
   */
  const handleBack = () => {
    if (controlFilterSwitch !== null) {
      controlFilterSwitch.controlSwitch.switchControl();
    }
  };

  // Reset the filter depth view whenever you switch to a different cabinet
  useEffect(() => {
    // passing null/nothing into the switchContorl method takes you to the top of the heirarchy
    controlFilterSwitch.controlSwitch.switchControl();
  }, [cabinetFilterOptionsActions, cabinetIdentifier, filterDefinitions]);

  /**
   *  Clear selected filters
   */
  const handleClear = () => {
    const newFilters = _objectSpread(_objectSpread({}, (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}), {}, {
      text: '',
      aiSearch: '',
      metaDeleted: 0 // Reset to default instead of deleting
    });
    filterDefinitions.forEach(_ref2 => {
      let index = _ref2.index;
      if (newFilters[index]) {
        delete newFilters[index];
      }
    });
    return serverFilter(newFilters, indexQuery);
  };
  const filterTextClear = () => filterText();

  /**
   *  Handle the metaDeleted filter change
   */
  const handleMetaDeletedChange = value => {
    const newFilters = _objectSpread(_objectSpread({}, (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}), {}, {
      metaDeleted: value
    });
    if (indexQuery) serverFilter(newFilters, indexQuery);
  };
  const filterAISearchClear = () => {
    // Clear AI search and any errors
    const newFilters = _objectSpread(_objectSpread({}, (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}), {}, {
      aiSearch: ''
    });
    if (indexQuery) {
      serverFilter(newFilters, indexQuery);
    }
  };

  // Rail summary labels: not memoized so we always read latest options list after fetch.
  const getFilterTitles = idx => {
    var _bucket$idx;
    const bucket = cabinetFilterOptions[cabinetIdentifier] || cabinetFilterOptions[cabinetFilterOptions === null || cabinetFilterOptions === void 0 ? void 0 : cabinetFilterOptions.currentCabinet] || {};
    const currentCabinetFilterOptions = ((_bucket$idx = bucket[idx]) === null || _bucket$idx === void 0 ? void 0 : _bucket$idx.list) || [];
    const _ref3 = indexQuery || {},
      _ref3$filters = _ref3.filters,
      filters = _ref3$filters === void 0 ? {} : _ref3$filters;
    const currentSelections = filters[idx] || [];
    const titles = [];
    currentSelections.forEach(selection => {
      if (Array.isArray(selection)) {
        if (selection.length === 1) {
          const id = selection[0];
          if (id === null || typeof id === 'string' || typeof id === 'number') {
            if (id === null) {
              titles.push('No selection');
            } else {
              const opt = currentCabinetFilterOptions.find(option => option.id === id || option.value === id) || {};
              const title = opt.name || id;
              titles.push(title);
            }
          }
        } else {
          selection.forEach(name => {
            if (typeof name === 'string') {
              titles.push(name);
            }
          });
        }
      }
    });
    return titles;
  };

  // Prefetch options for filters that already have selections (URL, saved view) so lists
  // load without drill-in. Separate from UUID→display-name normalization.
  useEffect(() => {
    const filters = indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters;
    if (!cabinetIdentifier || !filters) {
      return undefined;
    }
    filterPrefetchRunIdRef.current += 1;
    const runId = filterPrefetchRunIdRef.current;
    const maybePrefetchOne = async filterDef => {
      const idx = filterDef.index;
      const raw = filters[idx];
      if (!Array.isArray(raw) || raw.length === 0) {
        return;
      }
      const selectionNeedsLookup = raw.some(entry => Array.isArray(entry) && entry.length === 1 && entry[0] !== null && entry[0] !== '' && (typeof entry[0] === 'string' || typeof entry[0] === 'number'));
      if (!selectionNeedsLookup) {
        return;
      }
      const bucket = cabinetFilterOptions[cabinetIdentifier] || cabinetFilterOptions[cabinetFilterOptions === null || cabinetFilterOptions === void 0 ? void 0 : cabinetFilterOptions.currentCabinet] || {};
      const slot = bucket[idx] || {};
      const error = slot.error,
        _slot$indexQuery = slot.indexQuery,
        filterOptionsIndexQuery = _slot$indexQuery === void 0 ? {} : _slot$indexQuery,
        _slot$list = slot.list,
        list = _slot$list === void 0 ? [] : _slot$list,
        _slot$loading = slot.loading,
        slotLoading = _slot$loading === void 0 ? false : _slot$loading;
      if (slotLoading || error) {
        return;
      }
      if (list.length > 0) {
        return;
      }
      if (filterOptionsIndexQuery.initialized && filterOptionsIndexQuery.cabinet === cabinetIdentifier && filterOptionsIndexQuery.filterIndex === idx && !filterOptionsIndexQuery.error && !error) {
        return;
      }
      await cabinetFilterOptionsActions.serverIndex(cabinetIdentifier, idx, filterOptionsIndexQuery);
    };
    filterPrefetchTailRef.current = filterPrefetchTailRef.current.then(async () => {
      /* eslint-disable no-await-in-loop -- cabinetFilterOptions shares entityActionFactory's
         single-slot pendingQueryQueue; parallel serverIndex drops queued work. */
      for (let i = 0; i < filterDefinitions.length; i += 1) {
        if (runId !== filterPrefetchRunIdRef.current) {
          return;
        }
        await maybePrefetchOne(filterDefinitions[i]);
      }
    }).catch(() => {});
    return undefined;
  }, [cabinetFilterOptions, cabinetFilterOptionsActions, cabinetIdentifier, filterDefinitions, indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters]);
  const replaceFilters = async savedView => {
    await serverReplaceView(buildCabinetReplaceViewQuery({
      indexQuery,
      initialIndexQuery: {},
      savedView,
      formTypeIdentifiers,
      cabinetIdentifier,
      filterDefinitions
    }));
    handleBack();
  };

  // Create Show Deleted filter control (for admin users)
  const showDeletedFilterControl = isAdminUser ? {
    label: 'Show Deleted',
    entity: 'ShowDeleted',
    filterName: 'metaDeleted',
    control: /*#__PURE__*/React.createElement(ShowDeletedFilter, {
      value: (_indexQuery$filters$m = indexQuery === null || indexQuery === void 0 || (_indexQuery$filters = indexQuery.filters) === null || _indexQuery$filters === void 0 ? void 0 : _indexQuery$filters.metaDeleted) !== null && _indexQuery$filters$m !== void 0 ? _indexQuery$filters$m : 0,
      onChange: handleMetaDeletedChange,
      onBack: handleBack
    }),
    titleValues: [(indexQuery === null || indexQuery === void 0 || (_indexQuery$filters2 = indexQuery.filters) === null || _indexQuery$filters2 === void 0 ? void 0 : _indexQuery$filters2.metaDeleted) === 1 ? 'Yes' : 'No'],
    filters: (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}
  } : null;

  // create a CabinetFilter component for each filterDefinition
  const cabinetFilterControls = filterDefinitions.map(filterDef => ({
    filterName: filterDef.index,
    label: filterDef.title,
    entity: "".concat(cabinetIdentifier, "_").concat(filterDef.index),
    control: /*#__PURE__*/React.createElement(CabinetFilter, {
      cabinetIdentifier: cabinetIdentifier,
      filterDefinition: filterDef,
      indexQuery: indexQuery || {
        filters: {}
      },
      onBack: handleBack,
      serverFilter: serverFilter
    }),
    titleValues: () => getFilterTitles(filterDef.index),
    filters: (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}
  }));

  // Combine filters - Show Deleted first (if admin), then cabinet filters
  const filterControls = showDeletedFilterControl ? [showDeletedFilterControl, ...cabinetFilterControls] : cabinetFilterControls;
  const searchLabel = /*#__PURE__*/React.createElement(FullTextInstructions, null);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, {
    "data-testid": "cabinet-filter-panel"
  }, /*#__PURE__*/React.createElement(CardHeader, {
    className: "card-heading"
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    xs: 8
  }, /*#__PURE__*/React.createElement("h3", {
    className: "t-ifp-title"
  }, "Filter")), /*#__PURE__*/React.createElement(Col, {
    xs: 4
  }, /*#__PURE__*/React.createElement(Button, {
    size: "sm",
    onClick: handleClear,
    "data-testid": "t-cf-clear",
    className: "float-md-right t-cf-clear"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "times-circle",
    weight: "far"
  }), " Clear")))), /*#__PURE__*/React.createElement(CardBody, null, mergedAiSearchConfig && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    ref: aiSearchButtonRef,
    onClick: handleAiSearchButtonClick,
    onMouseEnter: handleAiSearchButtonMouseEnter,
    onMouseLeave: handleAiSearchButtonMouseLeave,
    "data-testid": "t-cf-ai-search-button",
    block: true,
    className: "mb-3"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "magic-wand-sparkles"
  }), " ", mergedAiSearchConfig.buttonTitle), /*#__PURE__*/React.createElement(AiChatModal, {
    isOpen: isAiChatModalOpen,
    onToggle: closeAiChatModal,
    onSendMessage: filterAISearch,
    onClear: filterAISearchClear,
    messages: errorMessages,
    isLoading: aiRequest,
    label: /*#__PURE__*/React.createElement("span", null, mergedAiSearchConfig.title),
    placeholder: mergedAiSearchConfig.promptPlaceholder,
    text: (indexQuery === null || indexQuery === void 0 || (_indexQuery$filters3 = indexQuery.filters) === null || _indexQuery$filters3 === void 0 ? void 0 : _indexQuery$filters3.aiSearch) || '',
    waitingMessage: mergedAiSearchConfig.waitingMessage,
    emptyStateMessage: mergedAiSearchConfig.emptyStateMessage
  })), /*#__PURE__*/React.createElement(SearchInput, {
    className: "t-cf-search",
    type: "text",
    label: searchLabel,
    text: (indexQuery === null || indexQuery === void 0 || (_indexQuery$filters4 = indexQuery.filters) === null || _indexQuery$filters4 === void 0 ? void 0 : _indexQuery$filters4.text) || '',
    onChange: filterText,
    onClear: filterTextClear,
    placeholder: searchPlaceholder
  }), /*#__PURE__*/React.createElement(ControlFilterSwitch, {
    className: "t-cfp-filters",
    filterControls: filterControls,
    ref: input => {
      controlFilterSwitch = input;
    }
  }))), /*#__PURE__*/React.createElement(Card, {
    className: "filters"
  }, /*#__PURE__*/React.createElement(CardHeader, {
    className: "card-heading"
  }, /*#__PURE__*/React.createElement("h3", null, "Saved Filters")), /*#__PURE__*/React.createElement(CardBody, null, /*#__PURE__*/React.createElement(SavedFormFilters, {
    cabinetIdentifier: cabinetIdentifier,
    cabinet: currentCabinet,
    replaceFilters: replaceFilters,
    filterDefinitions: filterDefinitions
  }))));
};
CabinetFilterPanel.propTypes = {
  cabinetIdentifier: PropTypes.string.isRequired,
  formTypeIdentifiers: PropTypes.arrayOf(PropTypes.string),
  filterDefinitions: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.string,
    title: PropTypes.string
  })).isRequired,
  currentCabinet: PropTypes.shape({
    indexQuery: PropTypes.shape({
      filters: PropTypes.shape({
        text: PropTypes.string,
        aiSearch: PropTypes.string,
        formTypeIdentifiers: PropTypes.arrayOf(PropTypes.string),
        metaDeleted: PropTypes.number
      })
    }),
    initialIndexQuery: PropTypes.shape({}),
    aiError: PropTypes.string,
    aiRequest: PropTypes.bool,
    aiSuccess: PropTypes.bool
  }).isRequired,
  cabinetFilterOptions: PropTypes.shape({
    currentCabinet: PropTypes.string
  }).isRequired,
  cabinetFilterOptionsActions: PropTypes.shape({
    serverIndexes: PropTypes.func,
    serverIndex: PropTypes.func
  }).isRequired,
  cabinetActions: PropTypes.shape({
    serverReplaceView: PropTypes.func,
    serverFilter: PropTypes.func,
    serverIndex: PropTypes.func
  }).isRequired,
  searchPlaceholder: PropTypes.string,
  aiSearchConfig: PropTypes.shape({
    buttonTitle: PropTypes.string,
    promptPlaceholder: PropTypes.string,
    title: PropTypes.string,
    waitingMessage: PropTypes.string,
    emptyStateMessage: PropTypes.string
  }),
  username: PropTypes.string
};
export { CabinetFilterPanel as TestCabinetFilterPanel };
const cabinetFilterOptionsLayoutRevision = (state, ownProps) => {
  var _state$cabinetFilterO;
  const cid = ownProps.cabinetIdentifier;
  if (!cid) {
    return '';
  }
  const branch = (_state$cabinetFilterO = state.cabinetFilterOptions) === null || _state$cabinetFilterO === void 0 ? void 0 : _state$cabinetFilterO[cid];
  if (!branch || typeof branch !== 'object') {
    return '';
  }
  return Object.keys(branch).sort().map(fk => {
    const slot = branch[fk];
    if (!slot || typeof slot !== 'object') {
      return "".concat(fk, ":0:-");
    }
    const len = Array.isArray(slot.list) ? slot.list.length : 0;
    const load = slot.loading ? 'L' : '-';
    return "".concat(fk, ":").concat(len, ":").concat(load);
  }).join('|');
};
export default entityComposition(CabinetFilterPanel, ['cabinetFilterOptions'], {
  currentCabinet: state => {
    const _state$cabinet = state.cabinet,
      cabinet = _state$cabinet === void 0 ? {} : _state$cabinet;
    const currentCabinet = cabinet[cabinet.currentCabinet] || {};
    return _objectSpread(_objectSpread({}, currentCabinet), {}, {
      aiError: currentCabinet.aiError || null,
      aiRequest: currentCabinet.aiRequest || false,
      aiSuccess: currentCabinet.aiSuccess || false
    });
  },
  cabinetFilterOptionsLayoutRevision
}, ['cabinet']);